import { useUploadCrunch } from '@/app/(dashboard)/upload/UploadCrunchProvider'
import {
  Button,
  FormControl,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectTrigger,
  SelectContent,
  SelectValue,
  SelectItem,
  Spinner,
  BaseAlert,
  useFSTrackEvent,
} from '@/dc-shared-web/src'
import { zodResolver } from '@hookform/resolvers/zod'
import { FC, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import { step2Schema } from '@/app/(dashboard)/upload/step2/Step2Schema'
import { useOfficeSuggestedChecklists } from '@/dc-shared-web/src/hooks/api/office/useOfficeSuggestedChecklists'
import { DocumentTypeOptions } from '@/app/(dashboard)/upload/step2/DocumentTypeOptions'
import { faExclamationCircle } from '@document-crunch/pro-regular-svg-icons'
import {
  UploadDocumentParams,
  UploadDocumentResponse,
} from '@/dc-shared-web/src/hooks/api/document/uploadDocument'
import { FS, FS_CATEGORY } from '@/dc-shared-web/src/lib/trackingConstants'
import { useQueryClient } from 'react-query'
import {
  DOCUMENTS_QUERY_KEY,
  FOLDERS_QUERY_KEY,
  LIST_QUERY_KEY,
  PROJECT_QUERY_KEY,
  PROJECTS_QUERY_KEY,
} from '@/dc-shared-web/src/hooks/api/queryKeys'
import { SelectedFile } from '@/app/(dashboard)/upload/step2/SelectedFile'
import clsx from 'clsx'
import { UNIVERSAL_SOLUTION_TYPE_NAME } from '@/dc-shared-web/src/hooks/api/document/constants'

export type Step2Props = {
  teamUuid: string
  onUpload: (
    uploadParams: UploadDocumentParams
  ) => Promise<UploadDocumentResponse>
  isUploading: boolean
  errorMessage?: string
}

export const Step2: FC<Step2Props> = ({
  teamUuid,
  onUpload,
  isUploading,
  errorMessage,
}) => {
  const queryClient = useQueryClient()
  const { trackEvent } = useFSTrackEvent()

  const { data: suggestedChecklistsByDocumentType, isLoading } =
    useOfficeSuggestedChecklists({
      officeUUID: teamUuid,
    })

  const form = useForm<z.infer<typeof step2Schema>>({
    resolver: zodResolver(step2Schema),
    defaultValues: {
      documentTypeUuid: undefined,
      checklists: [],
      playbooks: [],
      assignments: [],
    },
  })

  const { nextStep, updateStepsData, stepsData, prevStep } = useUploadCrunch()

  const onSubmit = async () => {
    if (!form.formState.isValid || !stepsData.step1.file) return

    const formValues = form.getValues()

    updateStepsData('step2', formValues)

    const uploadParams: UploadDocumentParams = {
      file: stepsData.step1.file,
      officeUuid: teamUuid,
      documentTypeUuid: formValues.documentTypeUuid,
      checklists: formValues.checklists,
      assignments: formValues.assignments,
    }

    /* A crunch can only have one playbook so we take the first one. For simplicity, we make it an array. */
    const playbookUuid = formValues.playbooks[0]

    if (playbookUuid) {
      uploadParams.playbookUuid = playbookUuid
    }

    const locationId = stepsData.step1.location?.id.toString()

    /* We assign the locationId to the projectUuid or folderUuid based on the location type. If location type is dashboard, we don't assign any of these. */
    if (stepsData.step1.location?.type === 'project') {
      uploadParams.projectUuid = locationId
    }

    if (stepsData.step1.location?.type === 'folder') {
      uploadParams.folderUuid = locationId
    }

    try {
      await onUpload(uploadParams)

      const selectedDocumentType = suggestedChecklistsByDocumentType?.find(
        (docType) => docType.docTypeUuid === formValues.documentTypeUuid
      )

      const checklistNames =
        selectedDocumentType?.suggestedChecklists
          ?.filter(
            (checklist) =>
              checklist.type === 'checklist' &&
              formValues.checklists.includes(checklist.uuid)
          )
          ?.map((checklist) => checklist.name) || []

      trackEvent(FS.UPLOAD_SUCCESS, {
        solutionType: UNIVERSAL_SOLUTION_TYPE_NAME,
        documentType: selectedDocumentType?.docTypeName,
        checklistNames,
        checklistCount: formValues.checklists.length,
        playbookCount: formValues.playbooks.length,
      })

      nextStep()

      await invalidateQueries()
    } catch (error) {
      trackEvent(FS.UPLOAD_ERROR)
    }
  }

  /**
   * Refetch crunches table documents + folders/projects (which have crunch counts)
   */
  const invalidateQueries = () => {
    return Promise.allSettled([
      queryClient.invalidateQueries([DOCUMENTS_QUERY_KEY, LIST_QUERY_KEY]),
      queryClient.invalidateQueries([FOLDERS_QUERY_KEY]),
      queryClient.invalidateQueries([PROJECTS_QUERY_KEY]),
      queryClient.invalidateQueries([PROJECT_QUERY_KEY]),
    ])
  }

  const isFormValid = form.formState.isValid

  const selectedDocumentTypeUuid = useWatch({
    control: form.control,
    name: 'documentTypeUuid',
  })

  const documentType = useMemo(() => {
    return suggestedChecklistsByDocumentType?.find(
      (checklist) => checklist.docTypeUuid === selectedDocumentTypeUuid
    )
  }, [selectedDocumentTypeUuid, suggestedChecklistsByDocumentType])

  return (
    <div className='tw-space-y-4'>
      <div className='tw-space-y-4'>
        {stepsData.step1.file ? (
          <SelectedFile file={stepsData.step1.file} />
        ) : null}
        {isLoading ? (
          <Spinner />
        ) : suggestedChecklistsByDocumentType?.length ? (
          <div className={clsx('tw-space-y-2', isUploading && 'tw-opacity-50')}>
            <Form {...form}>
              <FormField
                control={form.control}
                name='documentTypeUuid'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Select a document type</FormLabel>
                    <div>
                      <FormControl>
                        <Select
                          onValueChange={(nextDocumentTypeUuid) => {
                            field.onChange(nextDocumentTypeUuid)

                            form.setValue('checklists', [])
                            form.setValue('playbooks', [])
                            form.setValue('assignments', [])

                            const selectedDocumentType =
                              suggestedChecklistsByDocumentType?.find(
                                (docType) =>
                                  docType.docTypeUuid === nextDocumentTypeUuid
                              )

                            const itemIndex =
                              suggestedChecklistsByDocumentType?.findIndex(
                                (docType) =>
                                  docType.docTypeUuid === nextDocumentTypeUuid
                              ) ?? -1

                            trackEvent(FS.DROPDOWN_OPTION_SELECTED, {
                              name:
                                selectedDocumentType?.docTypeName || 'Unknown',
                              parent: 'UploadDocumentType',
                              page_category: FS_CATEGORY.UPLOAD,
                              item_length:
                                suggestedChecklistsByDocumentType?.length || 0,
                              item_order: itemIndex + 1,
                            })
                          }}
                          onOpenChange={(isOpen) => {
                            if (isOpen) {
                              trackEvent(FS.DROPDOWN_CLICKED, {
                                name: 'UploadDocumentType',
                                text: 'Document Type',
                                page_category: FS_CATEGORY.UPLOAD,
                              })
                            }
                          }}
                          value={field.value}
                          disabled={isUploading}
                        >
                          <SelectTrigger
                            className='tw-w-full'
                            data-testid='document-type-dropdown'
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className='tw-z-[51]'>
                            {suggestedChecklistsByDocumentType?.map(
                              (documentType) => (
                                <SelectItem
                                  key={documentType.docTypeUuid}
                                  value={documentType.docTypeUuid}
                                  data-testid='document-type-item'
                                >
                                  {documentType.docTypeName}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            </Form>
            {documentType && (
              <DocumentTypeOptions
                documentType={documentType}
                form={form}
                officeUUID={teamUuid}
                projectId={
                  stepsData.step1.location?.type === 'project'
                    ? stepsData.step1.location?.id.toString()
                    : undefined
                }
                disabled={isUploading}
              />
            )}
          </div>
        ) : (
          <BaseAlert
            variant='destructive'
            title='There are no Document Types associated for this team.'
            icon={faExclamationCircle}
            description={<p>Please contact your team admin.</p>}
          />
        )}
        {errorMessage && (
          <BaseAlert
            variant='destructive'
            title='File failed to upload'
            description={errorMessage}
            icon={faExclamationCircle}
          />
        )}
      </div>
      <div className='tw-flex tw-items-center tw-justify-end tw-gap-2'>
        <Button
          onClick={() => {
            trackEvent(FS.BUTTON_CLICKED, {
              name: 'Back',
              page_category: FS_CATEGORY.UPLOAD,
            })
            prevStep()
          }}
          variant='outline'
          disabled={isUploading}
          data-testid='back-button'
        >
          Back
        </Button>
        <Button
          disabled={!isFormValid || isUploading}
          onClick={() => {
            trackEvent(FS.BUTTON_CLICKED, {
              name: 'Crunch',
              page_category: FS_CATEGORY.UPLOAD,
            })
            form.handleSubmit(onSubmit)()
          }}
          type='button'
          data-testid='submit-button'
        >
          <div className='tw-flex tw-items-center tw-gap-2'>
            {isUploading && (
              <Spinner className='tw-text-white tw-border-2 tw-w-4 tw-h-4' />
            )}
            <span>{isUploading ? 'Uploading...' : 'Crunch'}</span>
          </div>
        </Button>
      </div>
    </div>
  )
}
